/**
 * Gift card recipient form — works with #add-item-form + cart/add.js (scripts.js).
 * Requires Shopify property names per gift card recipient docs.
 */
(function () {
  'use strict';

  var MAX_MESSAGE = 200;
  var MAX_NAME = 255;
  var MAX_SCHEDULE_DAYS = 90;

  function root() {
    return document.querySelector('[data-gift-card-recipient]');
  }

  function isEnabled(wrap) {
    var cb = wrap && wrap.querySelector('[data-recipient-enable]');
    return cb && cb.checked;
  }

  function syncMessageCount(wrap) {
    if (!wrap) return;
    var message = wrap.querySelector('[data-recipient-message]');
    var counter = wrap.querySelector('[data-recipient-message-count]');
    if (!message || !counter) return;
    var max = parseInt(message.getAttribute('maxlength'), 10) || MAX_MESSAGE;
    var len = message.value.length;
    counter.textContent = len + ' / ' + max;
    counter.classList.toggle('gift-card-recipient__char-count--near', len >= max - 20 && len < max);
    counter.classList.toggle('gift-card-recipient__char-count--max', len >= max);
  }

  function scrollFieldIntoView(el) {
    if (!el || typeof el.scrollIntoView !== 'function') return;
    var reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'center',
      inline: 'nearest',
    });
  }

  function fieldErrorEl(wrap, fieldKey) {
    return wrap ? wrap.querySelector('[data-recipient-field-error="' + fieldKey + '"]') : null;
  }

  function inputForFieldKey(wrap, fieldKey) {
    var selectors = {
      email: '[data-recipient-email]',
      name: '[data-recipient-name]',
      message: '[data-recipient-message]',
      'send-on': '[data-recipient-send-on]',
    };
    return wrap ? wrap.querySelector(selectors[fieldKey]) : null;
  }

  function clearFieldError(wrap, fieldKey) {
    if (!wrap) return;
    var slot = fieldErrorEl(wrap, fieldKey);
    if (!slot) return;
    slot.innerHTML = '';
    slot.setAttribute('hidden', '');
    slot.setAttribute('aria-hidden', 'true');
    var input = inputForFieldKey(wrap, fieldKey);
    if (input) input.removeAttribute('aria-invalid');
  }

  function clearFieldErrors(wrap) {
    if (!wrap) return;
    var slots = wrap.querySelectorAll('[data-recipient-field-error]');
    slots.forEach(function (slot) {
      slot.innerHTML = '';
      slot.setAttribute('hidden', '');
      slot.setAttribute('aria-hidden', 'true');
    });
    wrap.classList.remove('gift-card-recipient--error');
    wrap.querySelectorAll(
      '[data-recipient-email], [data-recipient-name], [data-recipient-message], [data-recipient-send-on]'
    ).forEach(function (input) {
      input.removeAttribute('aria-invalid');
      input.setCustomValidity('');
    });
  }

  function showFieldError(wrap, fieldKey, message, focusEl) {
    if (!wrap) return false;
    clearFieldErrors(wrap);
    var slot = fieldErrorEl(wrap, fieldKey);
    if (slot) {
      slot.innerHTML =
        '<i class="fas fa-exclamation-triangle" aria-hidden="true"></i>' +
        '<span class="addtocart-inline-warning__text"></span>';
      var textEl = slot.querySelector('.addtocart-inline-warning__text');
      if (textEl) textEl.textContent = message;
      slot.removeAttribute('hidden');
      slot.setAttribute('aria-hidden', 'false');
    }
    if (focusEl) {
      focusEl.setAttribute('aria-invalid', 'true');
      if (slot && slot.id) focusEl.setAttribute('aria-describedby', slot.id);
      scrollFieldIntoView(focusEl);
      if (typeof focusEl.focus === 'function') focusEl.focus();
    } else if (slot) {
      scrollFieldIntoView(slot);
    }
    wrap.classList.add('gift-card-recipient--error');
    return false;
  }

  function setFieldsEnabled(wrap, on) {
    if (!wrap) return;
    var inputs = wrap.querySelectorAll(
      '[data-recipient-flag], [data-recipient-offset], [data-recipient-email], [data-recipient-name], [data-recipient-message], [data-recipient-send-on]'
    );
    inputs.forEach(function (el) {
      el.disabled = !on;
    });
    setRecipientPanelOpen(wrap, on);
    if (on) {
      var email = wrap.querySelector('[data-recipient-email]');
      if (email) email.required = true;
      var offset = wrap.querySelector('[data-recipient-offset]');
      if (offset) offset.value = String(new Date().getTimezoneOffset());
      syncMessageCount(wrap);
      var fields = wrap.querySelector('[data-recipient-fields]');
      var scrollTarget = fields || email || wrap;
      requestAnimationFrame(function () {
        scrollFieldIntoView(scrollTarget);
      });
    } else {
      clearRecipientFields(wrap);
    }
    clearFieldErrors(wrap);
  }

  function clearRecipientFields(wrap) {
    if (!wrap) return;
    var email = wrap.querySelector('[data-recipient-email]');
    var name = wrap.querySelector('[data-recipient-name]');
    var message = wrap.querySelector('[data-recipient-message]');
    var sendOn = wrap.querySelector('[data-recipient-send-on]');
    var offset = wrap.querySelector('[data-recipient-offset]');
    if (email) {
      email.value = '';
      email.required = false;
      email.setCustomValidity('');
      email.removeAttribute('aria-invalid');
    }
    if (name) name.value = '';
    if (message) message.value = '';
    if (sendOn) sendOn.value = '';
    if (offset) offset.value = '';
    syncMessageCount(wrap);
  }

  /** HTML5 email validation (type="email" + checkValidity), aligned with Shopify Dawn recipient form. */
  function validateRecipientEmail(emailInput) {
    if (!emailInput) {
      return { ok: false, message: 'Please enter a recipient email address.' };
    }
    emailInput.required = true;
    emailInput.value = emailInput.value.trim();
    emailInput.setCustomValidity('');
    if (!emailInput.value) {
      return { ok: false, message: 'Please enter a recipient email address.' };
    }
    if (!emailInput.checkValidity()) {
      var msg = emailInput.validationMessage || 'Please enter a valid recipient email address.';
      if (emailInput.validity.typeMismatch) {
        msg = 'Please enter a valid recipient email address.';
      } else if (emailInput.validity.valueMissing) {
        msg = 'Please enter a recipient email address.';
      }
      return { ok: false, message: msg };
    }
    return { ok: true };
  }

  function parseSendOn(value) {
    if (!value || !value.trim()) return null;
    var parts = value.trim().split('-');
    if (parts.length !== 3) return null;
    var y = parseInt(parts[0], 10);
    var m = parseInt(parts[1], 10) - 1;
    var d = parseInt(parts[2], 10);
    var dt = new Date(y, m, d);
    if (dt.getFullYear() !== y || dt.getMonth() !== m || dt.getDate() !== d) return null;
    return dt;
  }

  function startOfDay(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  /** Local calendar date for <input type="date"> (avoid UTC shift from toISOString). */
  function formatLocalDateYmd(d) {
    var y = d.getFullYear();
    var m = d.getMonth() + 1;
    var day = d.getDate();
    return y + '-' + (m < 10 ? '0' : '') + m + '-' + (day < 10 ? '0' : '') + day;
  }

  function setRecipientPanelOpen(wrap, on) {
    if (!wrap) return;
    var fields = wrap.querySelector('[data-recipient-fields]');
    var cb = wrap.querySelector('[data-recipient-enable]');
    if (fields) {
      if (on) {
        fields.removeAttribute('hidden');
        fields.setAttribute('aria-hidden', 'false');
      } else {
        fields.setAttribute('hidden', '');
        fields.setAttribute('aria-hidden', 'true');
      }
    }
    if (cb) cb.setAttribute('aria-expanded', on ? 'true' : 'false');
  }

  function normalizeRecipientValues(wrap) {
    if (!wrap || !isEnabled(wrap)) return;
    var email = wrap.querySelector('[data-recipient-email]');
    var name = wrap.querySelector('[data-recipient-name]');
    var message = wrap.querySelector('[data-recipient-message]');
    var sendOn = wrap.querySelector('[data-recipient-send-on]');
    if (email) email.value = email.value.trim();
    if (name) name.value = name.value.trim();
    if (message) message.value = message.value.trim();
    if (sendOn) sendOn.value = sendOn.value.trim();
  }

  function bindFieldErrorClear(wrap, fieldKey, inputSelector) {
    var input = wrap.querySelector(inputSelector);
    if (!input) return;
    input.addEventListener('input', function () {
      input.removeAttribute('aria-invalid');
      input.setCustomValidity('');
      clearFieldError(wrap, fieldKey);
      if (!wrap.querySelector('[data-recipient-field-error]:not([hidden])')) {
        wrap.classList.remove('gift-card-recipient--error');
      }
    });
  }

  function validate(wrap) {
    if (!wrap || !isEnabled(wrap)) return true;

    var email = wrap.querySelector('[data-recipient-email]');
    var name = wrap.querySelector('[data-recipient-name]');
    var message = wrap.querySelector('[data-recipient-message]');
    var sendOn = wrap.querySelector('[data-recipient-send-on]');

    var emailCheck = validateRecipientEmail(email);
    if (!emailCheck.ok) {
      if (email) email.setCustomValidity(emailCheck.message);
      return showFieldError(wrap, 'email', emailCheck.message, email);
    }

    if (name && name.value.length > MAX_NAME) {
      return showFieldError(wrap, 'name', 'Recipient name cannot be longer than 255 characters.', name);
    }

    if (message && message.value.length > MAX_MESSAGE) {
      return showFieldError(wrap, 'message', 'Message cannot be longer than 200 characters.', message);
    }

    if (sendOn && sendOn.value.trim()) {
      var chosen = parseSendOn(sendOn.value);
      if (!chosen) {
        return showFieldError(wrap, 'send-on', 'Please enter a valid send date.', sendOn);
      }
      var today = startOfDay(new Date());
      var max = new Date(today);
      max.setDate(max.getDate() + MAX_SCHEDULE_DAYS);
      var chosenDay = startOfDay(chosen);
      if (chosenDay < today) {
        return showFieldError(wrap, 'send-on', 'Send date cannot be in the past.', sendOn);
      }
      if (chosenDay > max) {
        return showFieldError(
          wrap,
          'send-on',
          'Send date can be scheduled up to 90 days in the future.',
          sendOn
        );
      }
    }

    var offset = wrap.querySelector('[data-recipient-offset]');
    if (offset) offset.value = String(new Date().getTimezoneOffset());

    normalizeRecipientValues(wrap);
    clearFieldErrors(wrap);
    return true;
  }

  function bind(wrap) {
    if (!wrap || wrap.getAttribute('data-recipient-bound') === 'true') return;
    wrap.setAttribute('data-recipient-bound', 'true');

    var cb = wrap.querySelector('[data-recipient-enable]');
    if (cb) {
      if (typeof window.motSyncAddonToggleCheckedLabel === 'function') {
        window.motSyncAddonToggleCheckedLabel(cb);
      }
      cb.addEventListener('change', function () {
        setFieldsEnabled(wrap, cb.checked);
        if (typeof window.motSyncAddonToggleCheckedLabel === 'function') {
          window.motSyncAddonToggleCheckedLabel(cb);
        }
      });
    }

    var sendOn = wrap.querySelector('[data-recipient-send-on]');
    if (sendOn) {
      var today = startOfDay(new Date());
      var maxDate = new Date(today);
      maxDate.setDate(maxDate.getDate() + MAX_SCHEDULE_DAYS);
      sendOn.setAttribute('min', formatLocalDateYmd(today));
      sendOn.setAttribute('max', formatLocalDateYmd(maxDate));
    }

    setRecipientPanelOpen(wrap, isEnabled(wrap));

    bindFieldErrorClear(wrap, 'email', '[data-recipient-email]');
    bindFieldErrorClear(wrap, 'name', '[data-recipient-name]');
    bindFieldErrorClear(wrap, 'message', '[data-recipient-message]');
    bindFieldErrorClear(wrap, 'send-on', '[data-recipient-send-on]');

    var message = wrap.querySelector('[data-recipient-message]');
    if (message) {
      syncMessageCount(wrap);
      message.addEventListener('input', function () {
        syncMessageCount(wrap);
      });
    }
  }

  function init() {
    var wrap = root();
    if (wrap) bind(wrap);
  }

  window.motValidateGiftCardRecipient = function () {
    var wrap = root();
    if (!wrap) return true;
    return validate(wrap);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
